/**
 * PythonVariablesHelper - Helper for managing Python variables and globals
 *
 * This helper provides centralized management of Python global variables,
 * namespace operations, and data exchange between Python and JavaScript.
 */

import { PythonUtils } from '../../../helpers/python-utils.helper';
import { GeneralHelpers } from '../../../helpers/general.helper';
import { Chunk } from '../../../interfaces/step/step.interface';
import { StepDataHelper } from '../../../helpers/step-data.helper';

export interface VariableInfo {
  name: string;
  type: string;
  value: any;
  size?: number;
}

export interface NamespaceInfo {
  globals: VariableInfo[];
  locals: VariableInfo[];
  builtins: string[];
  totalVariables: number;
}

export interface VariableOperationResult {
  success: boolean;
  variableName: string;
  operation: 'get' | 'set' | 'delete' | 'clear';
  value?: any;
  error?: string;
}

export interface VariablesConfig {
  enableLogging?: boolean;
  trackVariableChanges?: boolean;
  maxVariableSize?: number;
  excludeBuiltins?: boolean;
}

export class PythonVariablesHelper {
  private python: any;
  private config: VariablesConfig;
  private variableChangeTracker = new Map<string, any>();

  constructor(python: any, config: VariablesConfig = {}) {
    this.python = python;
    this.config = {
      enableLogging: false,
      trackVariableChanges: true,
      maxVariableSize: 1024 * 1024, // 1MB
      excludeBuiltins: true,
      ...config
    };
  }

  /**
   * Get a global variable from Python
   */
  getGlobalVariable(variableName: string): VariableOperationResult {
    try {

      if (!this.python?.globals) {
        throw new Error('Python globals not available');
      }

      const value = this.python.globals.get(variableName);

      if (value === undefined) {
        return {
          success: false,
          variableName,
          operation: 'get',
          error: `Variable '${variableName}' not found in globals`
        };
      }

      // Convert PyProxy to JavaScript if needed
      let jsValue = value;
      if (value && typeof value.toJs === 'function') {
        jsValue = value.toJs();
        value.destroy?.(); // Clean up PyProxy
      }

      return {
        success: true,
        variableName,
        operation: 'get',
        value: jsValue
      };

    } catch (error: any) {
      return {
        success: false,
        variableName,
        operation: 'get',
        error: error.message
      };
    }
  }

  /**
   * Set a global variable in Python
   */
  setGlobalVariable(variableName: string, value: any): VariableOperationResult {
    try {

      if (!this.python?.globals) {
        throw new Error('Python globals not available');
      }

      // Track variable changes if enabled
      if (this.config.trackVariableChanges) {
        const oldValue = this.python.globals.get(variableName);
        this.variableChangeTracker.set(variableName, oldValue);
      }

      this.python.globals.set(variableName, value);

      return {
        success: true,
        variableName,
        operation: 'set',
        value
      };

    } catch (error: any) {
      return {
        success: false,
        variableName,
        operation: 'set',
        error: error.message
      };
    }
  }

  /**
   * Delete a global variable from Python
   */
  deleteGlobalVariable(variableName: string): VariableOperationResult {
    try {

      if (!this.python?.globals) {
        throw new Error('Python globals not available');
      }

      // Track variable changes if enabled
      if (this.config.trackVariableChanges) {
        const oldValue = this.python.globals.get(variableName);
        this.variableChangeTracker.set(variableName, oldValue);
      }

      this.python.globals.delete(variableName);

      return {
        success: true,
        variableName,
        operation: 'delete'
      };

    } catch (error: any) {
      return {
        success: false,
        variableName,
        operation: 'delete',
        error: error.message
      };
    }
  }

  /**
   * Clear all global variables (except builtins)
   */
  clearGlobalVariables(): VariableOperationResult {
    try {

      const clearCode = `
import sys

# Get list of user-defined variables to clear
vars_to_clear = []
for name in list(globals().keys()):
    if not name.startswith('_') and name not in dir(__builtins__):
        vars_to_clear.append(name)

# Clear variables
cleared_count = 0
for var_name in vars_to_clear:
    try:
        del globals()[var_name]
        cleared_count += 1
    except:
        pass

cleared_count
      `;

      const clearedCount = this.python.runPython(clearCode);

      return {
        success: true,
        variableName: '__all__',
        operation: 'clear',
        value: clearedCount
      };

    } catch (error: any) {
      return {
        success: false,
        variableName: '__all__',
        operation: 'clear',
        error: error.message
      };
    }
  }

  /**
   * Get information about all variables in the current namespace
   */
  getNamespaceInfo(): NamespaceInfo {
    try {
      const namespaceCode = `
import sys
import json

def get_namespace_info():
    result = {
        'globals': [],
        'locals': [],
        'builtins': [],
        'total_variables': 0
    }

    # Get globals
    for name, value in globals().items():
        if not name.startswith('_') or not ${this.config.excludeBuiltins}:
            try:
                var_type = type(value).__name__
                var_size = sys.getsizeof(value) if hasattr(sys, 'getsizeof') else 0

                # Don't include very large objects in the summary
                if var_size < ${this.config.maxVariableSize}:
                    result['globals'].append({
                        'name': name,
                        'type': var_type,
                        'size': var_size,
                        'value': str(value)[:100] if len(str(value)) > 100 else str(value)
                    })
            except:
                pass

    # Get builtins
    try:
        result['builtins'] = list(dir(__builtins__))
    except:
        pass

    result['total_variables'] = len(result['globals'])
    return result

json.dumps(get_namespace_info())
      `;

      const resultJson = this.python.runPython(namespaceCode);
      const namespaceData = JSON.parse(resultJson);

      return {
        globals: namespaceData.globals || [],
        locals: namespaceData.locals || [],
        builtins: namespaceData.builtins || [],
        totalVariables: namespaceData.total_variables || 0
      };

    } catch (error: any) {
      console.warn('Python variables: failed to read namespace info', error);
      return {
        globals: [],
        locals: [],
        builtins: [],
        totalVariables: 0
      };
    }
  }

  /**
   * Set data variable with recursive JSON parsing
   */
  setDataVariable(data: any): VariableOperationResult {
    try {

      // Phase 2: Use PythonUtils instead of duplicate recursive_json_loads
      const dataSetupCode = PythonUtils.createDataVariableCode(data);

      const result = this.python.runPython(dataSetupCode);

      return {
        success: result === 'data_set',
        variableName: 'data',
        operation: 'set',
        value: data
      };

    } catch (error: any) {
      return {
        success: false,
        variableName: 'data',
        operation: 'set',
        error: error.message
      };
    }
  }

  /**
   * Get data variable and convert to JavaScript
   */
  getDataVariable(): VariableOperationResult {
    try {

      let globalVariables = this.python.globals.get('data');

      if (!globalVariables) {
        return {
          success: false,
          variableName: 'data',
          operation: 'get',
          error: 'Data variable not found'
        };
      }

      // Convert PyProxy to JavaScript
      if (globalVariables && typeof globalVariables.toJs === 'function') {
        const jsData = globalVariables.toJs();
        globalVariables.destroy();

        // Convert Map to Object if needed
        const finalData = this.convertMapToObject(jsData);

        return {
          success: true,
          variableName: 'data',
          operation: 'get',
          value: finalData
        };
      }

      return {
        success: true,
        variableName: 'data',
        operation: 'get',
        value: globalVariables
      };

    } catch (error: any) {
      return {
        success: false,
        variableName: 'data',
        operation: 'get',
        error: error.message
      };
    }
  }

  /**
   * Convert Map to Object recursively.
   * Pyodide converts Python None to JS undefined, but JSON.stringify silently
   * drops undefined properties. Convert undefined → null to preserve keys.
   */
  private convertMapToObject(data: any): any {
    if (data instanceof Map) {
      const obj: any = {};
      data.forEach((value, key) => {
        obj[key] = this.convertMapToObject(value === undefined ? null : value);
      });
      return obj;
    } else if (Array.isArray(data)) {
      return data.map(item => this.convertMapToObject(item === undefined ? null : item));
    } else if (data && typeof data === 'object') {
      const obj: any = {};
      for (const [key, value] of Object.entries(data)) {
        obj[key] = this.convertMapToObject(value === undefined ? null : value);
      }
      return obj;
    }
    return data;
  }

  /**
   * Get variable change history
   */
  getVariableChangeHistory(): Map<string, any> {
    return new Map(this.variableChangeTracker);
  }

  /**
   * Clear variable change history
   */
  clearVariableChangeHistory(): void {
    this.variableChangeTracker.clear();
  }

  /**
   * Check if Python runtime is properly initialized
   */
  isRuntimeReady(): boolean {
    return !!(this.python && this.python.globals && typeof this.python.globals.get === 'function');
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<VariablesConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current configuration
   */
  getConfig(): VariablesConfig {
    return { ...this.config };
  }

  /**
   * Get statistics about current variables
   */
  getVariableStats(): {
    totalGlobals: number;
    userDefinedVariables: number;
    trackedChanges: number;
    memoryUsage: number;
  } {
    const namespaceInfo = this.getNamespaceInfo();

    return {
      totalGlobals: namespaceInfo.totalVariables,
      userDefinedVariables: namespaceInfo.globals.filter(v => !v.name.startsWith('_')).length,
      trackedChanges: this.variableChangeTracker.size,
      memoryUsage: namespaceInfo.globals.reduce((sum, v) => sum + (v.size || 0), 0)
    };
  }

  /**
   * Get content with global variables replaced
   * @param content - Content string to process
   * @param projectVariablesService - Service for project variables
   * @returns Content with variables replaced
   */
  getContentWithVariablesReplaced(content: string, projectVariablesService: any): string {
    const chunkContent = GeneralHelpers.replaceGlobals(
      content,
      projectVariablesService.variableList
    );
    return chunkContent;
  }

  /**
   * Add global variables from notebook variables service, underlaid with
   * project constants (SCRUM-626): `project constant < workflow variable`,
   * mirroring enoch's headless merge.
   * @param chunkData - Chunk data with sortOrder
   * @param notebookVariablesService - Service with workflow variable list
   * @param projectVariablesService - Service with project constants list
   * @returns Object with global data variables
   */
  addGlobalVariables(
    chunkData: Chunk,
    notebookVariablesService: any,
    projectVariablesService?: any
  ): Record<string, unknown> {
    const globalDataVariables = StepDataHelper.buildStepDataMap(
      projectVariablesService?.variableList,
      notebookVariablesService?.variableList,
      chunkData.sortOrder,
      { alwaysIncludeJobArgs: true }
    );

    // Set data variable using setDataVariable method
    this.setDataVariable(globalDataVariables);

    return globalDataVariables;
  }
}